import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { UploadProvider } from "@/contexts/UploadContext";
import { UploadProgress } from "@/components/UploadProgress";
import { ProtectedRoute } from "@/components/ProtectedRoute";

// Eager load the landing page for fast initial render
import Welcome from "./pages/Welcome";

// Lazy load all other routes to reduce initial bundle size
const Library = lazy(() => import("./pages/Library"));
const Auth = lazy(() => import("./pages/Auth"));
const Sessions = lazy(() => import("./pages/Sessions"));
const Designer = lazy(() => import("./pages/Designer"));
const Clubs = lazy(() => import("./pages/Clubs"));
const ClubDetail = lazy(() => import("./pages/ClubDetail"));
const AUCoaches = lazy(() => import("./pages/AUCoaches"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Simple loading fallback that matches the app's background
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <UploadProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <UploadProgress />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Welcome />} />
                <Route path="/library" element={<Library />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/sessions" element={<Sessions />} />
                <Route path="/designer" element={<Designer />} />
                <Route path="/clubs" element={<Clubs />} />
                <Route path="/clubs/:clubId" element={<ClubDetail />} />
                <Route path="/au-coaches" element={<AUCoaches />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </UploadProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
